/**
 * The mailbox's sending addresses: pick one from the list, edit what it holds,
 * switch it on or off.
 *
 * It replaced the single "From address" field. The mailbox writes as the
 * company (`info@`) and as a person (`aimar@`), and the composer now has a From
 * field to pick between them. This is where that list is kept.
 *
 * ONE ADDRESS AT A TIME. The owner asked for a combobox that picks the address,
 * with that address's details under it, rather than every row open at once:
 * each row can carry its own SMTP login, and five open rows of host-less
 * logins read as five mailboxes.
 *
 * ONE OF THEM IS THE DEFAULT, and it is always switched on: a new letter starts
 * from it, and the server writes its own letters as it.
 *
 * Its own file because the card around it is long already and this block has
 * its own state (which address is open).
 */
import { useState } from "react";
import { Plus, Star, Trash2 } from "lucide-react";
import { Combobox, type ComboboxOption } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailSenderRow } from "../../../../lib/mail";
import { Btn, StatusPill } from "../../../ui";
import { Field, FieldGroup, Pill, SecretPill, ToggleRow } from "../panel/fields";

/** Ids only have to be unique within one mailbox; the API checks the shape. */
function newSenderId(): string {
  return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Loose on purpose: the server has the real check, this only flags a typo early. */
export function looksLikeAddress(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function SenderSettings({
  rows,
  defaultId,
  onChange,
  smtpMode,
  mailboxLogin,
}: {
  rows: MailSenderRow[];
  defaultId: string;
  onChange: (rows: MailSenderRow[], defaultId: string) => void;
  /** Own logins only mean something over SMTP. Resend sends any verified address. */
  smtpMode: boolean;
  /** The mailbox's own SMTP user, named in the hint as what an empty login uses. */
  mailboxLogin: string;
}) {
  const t = useT();
  // The stored default may be gone (removed in this very session), so the mark
  // follows the server's fallback: the first row.
  const activeDefault = rows.some((r) => r.id === defaultId) ? defaultId : rows[0]?.id ?? "";
  const [openId, setOpenId] = useState(activeDefault);
  const open = rows.find((r) => r.id === openId) ?? rows[0] ?? null;
  const defaultRow = rows.find((r) => r.id === activeDefault) ?? null;
  const isDefault = !!open && open.id === activeDefault;

  function patch(part: Partial<MailSenderRow>) {
    if (!open) return;
    onChange(rows.map((r) => (r.id === open.id ? { ...r, ...part } : r)), activeDefault);
  }

  function add() {
    const row: MailSenderRow = { id: newSenderId(), address: "", name: "", active: true, smtpUser: "", smtpPassSet: false };
    onChange([...rows, row], activeDefault || row.id);
    setOpenId(row.id);
  }

  function remove() {
    if (!open) return;
    const next = rows.filter((r) => r.id !== open.id);
    // Hand the mark on rather than leaving it pointing at nothing.
    const nextDefault = activeDefault === open.id ? next[0]?.id ?? "" : activeDefault;
    onChange(next, nextDefault);
    setOpenId(nextDefault);
  }

  const options: ComboboxOption[] = rows.map((r) => ({
    value: r.id,
    label: r.address.trim() || t("settings.mail.senderNew"),
    hint: [
      r.name.trim() || defaultRow?.name.trim() || "",
      r.id === activeDefault ? t("settings.mail.senderDefault") : r.active ? "" : t("settings.mail.senderOff"),
    ]
      .filter(Boolean)
      .join(" - "),
  }));

  const badAddress = !!open && open.address.trim() !== "" && !looksLikeAddress(open.address);

  return (
    <FieldGroup title={t("settings.mail.senders")}>
      <p className="crm-sub -mt-1 text-[12.5px]">{t("settings.mail.sendersHint")}</p>

      <div className="flex flex-wrap items-center gap-2.5">
        <div className="min-w-0 flex-1 basis-[240px]">
          {rows.length > 0 ? (
            <Combobox
              options={options}
              value={open?.id ?? ""}
              onChange={(id) => id && setOpenId(id)}
              className="crm-inp h-11 border-0 shadow-none"
              popoverClassName="rounded-2xl"
              aria-label={t("settings.mail.senderPick")}
              placeholder={t("settings.mail.senderPick")}
              searchPlaceholder={t("settings.mail.senderSearch")}
              emptyLabel={t("settings.mail.senderNone")}
            />
          ) : (
            <p className="crm-sub text-[12.5px]">{t("settings.mail.senderEmpty")}</p>
          )}
        </div>
        <Btn small type="button" onClick={add}>
          <Plus className="size-3.5" />
          {t("settings.mail.senderAdd")}
        </Btn>
      </div>

      {open ? (
        <div className="space-y-4 rounded-2xl border border-[var(--crm-hair)] p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={`${t("settings.mail.fromAddress")} *`}
              hint={badAddress ? t("settings.mail.senderInvalid") : undefined}
            >
              <Pill
                value={open.address}
                onChange={(v) => patch({ address: v })}
                type="email"
                placeholder={t("common.example.email")}
                autoComplete="off"
              />
            </Field>
            <Field label={t("settings.mail.fromName")} hint={isDefault ? undefined : t("settings.mail.senderNameHint")}>
              <Pill
                value={open.name}
                onChange={(v) => patch({ name: v })}
                // Shows the name an empty field borrows, so the owner sees what
                // the client's inbox will say without saving first.
                placeholder={(!isDefault && defaultRow?.name.trim()) || "Firma OÜ"}
                autoComplete="off"
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            <div className="min-w-0 flex-1 basis-[240px]">
              <ToggleRow
                label={t("settings.mail.senderActive")}
                hint={isDefault ? t("settings.mail.senderActiveDefault") : t("settings.mail.senderActiveHint")}
                checked={isDefault || open.active}
                disabled={isDefault}
                onChange={(v) => patch({ active: v })}
              />
            </div>
            {isDefault ? (
              <StatusPill tone="ok">{t("settings.mail.senderDefault")}</StatusPill>
            ) : (
              <Btn small type="button" onClick={() => onChange(rows.map((r) => (r.id === open.id ? { ...r, active: true } : r)), open.id)}>
                <Star className="size-3.5" />
                {t("settings.mail.senderMakeDefault")}
              </Btn>
            )}
          </div>

          {smtpMode ? (
            <div className="space-y-3 border-t border-dashed border-[var(--crm-hair)] pt-4">
              <div>
                <div className="text-[13px] font-[600]">{t("settings.mail.senderLogin")}</div>
                <p className="crm-sub !mt-0.5 text-[11.5px] leading-snug">
                  {t("settings.mail.senderLoginHint", { login: mailboxLogin || "-" })}
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("settings.mail.smtpUser")}>
                  <Pill
                    value={open.smtpUser}
                    onChange={(v) => patch({ smtpUser: v })}
                    placeholder={mailboxLogin}
                    autoComplete="off"
                  />
                </Field>
                <Field
                  label={t("settings.mail.smtpPass")}
                  hint={open.smtpPassSet && !open.smtpPass ? t("settings.mail.passwordKept") : undefined}
                >
                  <SecretPill
                    value={open.smtpPass ?? ""}
                    onChange={(v) => patch({ smtpPass: v })}
                    placeholder={open.smtpPassSet ? "••••••••" : ""}
                  />
                </Field>
              </div>
            </div>
          ) : null}

          <div className="flex justify-end">
            <Btn small type="button" onClick={remove}>
              <Trash2 className="size-3.5" />
              {t("settings.mail.senderRemove")}
            </Btn>
          </div>
        </div>
      ) : null}
    </FieldGroup>
  );
}
